<template>
  <base-material-card
    color="primary"
    icon="book"
    inline
    class="px-5 py-3"
  >
    <template v-slot:after-heading>
      <div class="text-h2 font-weight-light">
        {{ $t("specification_lines") }}
      </div>
    </template>

    <v-col>
      <v-row class="mx-0 justify-space-between">
        <v-col cols="6">
          <filter-main
            :filter-options="filterOptions"
            :filter-actions="filterActions"
            :filter="filter"
            @set-filter="setFilter"
          />
        </v-col>
        <v-btn
          :color="'primary'"
          @click="bulkDialog = true"
        >
          {{ $t("bulk_update", { count: selectedLines.length || totalItems }) }}
        </v-btn>
      </v-row>
      <v-divider class="my-3" />
      <v-data-table
        v-model="selectedLines"
        dense
        :items="items"
        :headers="headers"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        :footer-props="footerProps"
        show-select
      >
        <template v-slot:body.prepend>
          <filter-row
            :filter-options="filterOptions"
            :headers="headers"
            :filter="filter"
            show-select
            @set-filter="setFilter"
          />
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            :color="errorStatuses.includes(item.status) ? 'red' : 'green'"
            text-color="white"
            small
          >
            {{ $i18n.t(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.package_number="{ item }">
          <v-chip
            :color="'green'"
            text-color="white"
            small
          >
            {{ item.package_number }}
          </v-chip>
        </template>
        <template v-slot:item.title="{ item }">
          <v-chip
            :color="'orange'"
            text-color="white"
            small
          >
            {{ item.title }}
          </v-chip>
        </template>
        <template v-slot:item.filename="{ item }">
          <v-chip
            :color="'green'"
            text-color="white"
            small
          >
            {{ item.filename }}
          </v-chip>
        </template>
        <template v-slot:item.carrier_product_service="{ item }">
          <v-chip
            :color="'green'"
            text-color="white"
            small
          >
            {{ item.carrier }}_{{ item.carrier_product }}_{{
              item.carrier_service
            }}
          </v-chip>
        </template>
        <template v-slot:item.geo="{ item }">
          {{ item.country_from }}<v-icon>mdi-arrow-right</v-icon>{{ item.country_to }}
        </template>
        <template v-slot:item.total_price="{ item }">
          <v-dialog
            v-model="priceDialog[item.id]"
            width="500"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                text
                v-on="on"
              >
                {{ item.total_price }}
              </v-btn>
            </template>

            <v-card>
              <v-card-title
                class="text-h5 grey lighten-2 pb-4"
                primary-title
              >
                Prisgrundlag
              </v-card-title>

              <v-card-text>
                <v-simple-table
                  dense
                  class="mt-4 price-list"
                >
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th
                          width="100%"
                          class="display-1"
                        >
                          {{ $t("title") }}
                        </th>
                        <th class="display-1">
                          {{ $t("type") }}
                        </th>
                        <th class="display-1">
                          {{ $t("price") }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="price in item.prices"
                        :key="price.id"
                      >
                        <td>{{ price.title }}</td>
                        <td>{{ price.type }}</td>
                        <td>{{ price.price }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>

              <v-divider />

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="primary"
                  text
                  @click="priceDialog[item.id] = false"
                >
                  {{ $t("close") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
        <template v-slot:item.created_at="{ item }">
          {{ convertUTCToLocal(item.created_at) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            color="green"
            small
            class=""
            @click="editItem(item)"
          >
            <v-icon> mdi-pencil </v-icon>
          </v-btn>
        </template>
      </v-data-table>
      <v-dialog
        v-if="dialog"
        v-model="dialog"
        max-width="500px"
      >
        <v-card>
          <v-card-title>
            <span class="text-h5">{{ $t(name) }}</span>
          </v-card-title>

          <v-card-text>
            <v-container>
              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="editedItem.title"
                    :label="$t('title')"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="editedItem.package_number"
                    :label="$t('package_number')"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                >
                  <v-text-field
                    v-model="editedItem.total_price"
                    :label="$t('total') + ' ' + $t('price')"
                    suffix="DKK"
                    type="number"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                >
                  <v-text-field
                    v-model="editedItem.weight"
                    :label="$t('weight')"
                    suffix="g"
                    type="number"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                >
                  <v-text-field
                    v-model="editedItem.volume_weight"
                    :label="$t('volume_weight')"
                    type="number"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                >
                  <v-text-field
                    v-model="editedItem.carrier"
                    :label="$t('carrier')"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                >
                  <v-text-field
                    v-model="editedItem.carrier_product"
                    :label="$t('carrier') + ' ' + $t('product')"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                >
                  <v-text-field
                    v-model="editedItem.carrier_service"
                    :label="$t('carrier') + ' ' + $t('service')"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="editedItem.customer_id"
                    :label="$t('customer_id')"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="editedItem.customer_number"
                    :label="`${this.$t('carrier')} ${this.$t('customer_id')}`"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <base-paginated-select
                    :selected="{ iso: editedItem.country_from }"
                    endpoint="countries"
                    :label="`${$t('from')} ${$t('country')}`"
                    item-text="full_name"
                    selected-item-text="iso"
                    :rules="rules"
                    @value-change="(v) => (editedItem.country_from = v.iso)"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <base-paginated-select
                    :selected="{ iso: editedItem.country_to }"
                    endpoint="countries"
                    :label="`${$t('to')} ${$t('country')}`"
                    item-text="full_name"
                    selected-item-text="iso"
                    :rules="rules"
                    @value-change="(v) => (editedItem.country_to = v.iso)"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-autocomplete
                    :items="filterOptions.status.options"
                    :value="editedItem.status"
                    item-value="slug"
                    item-text="label"
                    :label="$t('status')"
                    @change="(v) => (editedItem.status = v)"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-autocomplete
                    :items="types"
                    :value="editedItem.type"
                    item-value="value"
                    item-text="label"
                    :label="$t('type')"
                    @change="(v) => (editedItem.type = v)"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              color="blue darken-1"
              text
              @click="close"
            >
              {{ $t("cancel") }}
            </v-btn>
            <v-btn
              color="blue darken-1"
              text
              :loading="saving"
              @click="save"
            >
              {{ $t("save") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-if="bulkDialog"
        v-model="bulkDialog"
        max-width="500px"
      >
        <v-card>
          <v-card-title>
            <span class="text-h5">{{ $t(name) }}</span>
          </v-card-title>

          <v-card-text>
            <v-container>
              <v-row>
                <v-col
                  cols="12"
                  sm="4"
                >
                  <v-text-field
                    v-model="bulkEditedItem.carrier"
                    :label="$t('carrier')"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                >
                  <v-text-field
                    v-model="bulkEditedItem.carrier_product"
                    :label="$t('carrier') + ' ' + $t('product')"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                >
                  <v-text-field
                    v-model="bulkEditedItem.carrier_service"
                    :label="$t('carrier') + ' ' + $t('service')"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="bulkEditedItem.customer_id"
                    :label="$t('customer_id')"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="bulkEditedItem.customer_number"
                    :label="`${this.$t('carrier')} ${this.$t('customer_id')}`"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <base-paginated-select
                    :selected="{ iso: bulkEditedItem.country_from }"
                    endpoint="countries"
                    :label="`${$t('from')} ${$t('country')}`"
                    item-text="full_name"
                    selected-item-text="iso"
                    :rules="rules"
                    @value-change="(v) => (bulkEditedItem.country_from = v.iso)"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <base-paginated-select
                    :selected="{ iso: bulkEditedItem.country_to }"
                    endpoint="countries"
                    :label="`${$t('to')} ${$t('country')}`"
                    item-text="full_name"
                    selected-item-text="iso"
                    :rules="rules"
                    @value-change="(v) => (bulkEditedItem.country_to = v.iso)"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-autocomplete
                    :items="filterOptions.status.options"
                    :value="bulkEditedItem.status"
                    item-value="slug"
                    item-text="label"
                    :label="$t('status')"
                    @change="(v) => (bulkEditedItem.status = v)"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-autocomplete
                    :items="types"
                    :value="bulkEditedItem.type"
                    item-value="value"
                    item-text="label"
                    :label="$t('type')"
                    @change="(v) => (bulkEditedItem.type = v)"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              color="blue darken-1"
              text
              @click="closeBulk"
            >
              {{ $t("cancel") }}
            </v-btn>
            <v-btn
              color="blue darken-1"
              text
              :loading="saving"
              @click="saveBulk"
            >
              {{ $t("save") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-col>
  </base-material-card>
</template>

<script>
  import List from '@/mixins/List'

  export default {
    mixins: [List],

    data (vm) {
      return {
        initLoad: true,
        dialog: false,
        bulkDialog: false,
        priceDialog: {},
        endpoint: 'specifications/lines',
        name: 'specification_lines',
        shortPoll: null,
        loading: false,
        cancelToken: null,
        types: [],
        errorStatuses: [
          'customer_not_found',
          'error',
          'missing_erp_number',
          'order_not_payed',
        ],
        filter: {
          status: [
            'customer_not_found',
            'error',
            'missing_erp_number',
            'order_not_payed',
            'duplicate_invoice',
          ],
        },
        headers: [
          {
            sortable: false,
            text: this.$t('id'),
            value: 'id',
          },
          {
            sortable: false,
            text: this.$t('status'),
            value: 'status',
          },
          {
            sortable: false,
            text: this.$t('package_number'),
            value: 'package_number',
          },
          {
            sortable: false,
            text: this.$t('title'),
            value: 'title',
            width: '50%',
          },
          {
            sortable: false,
            text: this.$t('filename'),
            value: 'filename',
            width: '50%',
          },
          {
            sortable: false,
            text: this.$t('line'),
            value: 'specification_file_line',
            align: 'center',
          },
          {
            sortable: false,
            text: this.$t('cps'),
            value: 'carrier_product_service',
          },
          {
            sortable: false,
            text: `${this.$t('geo')}`,
            value: 'geo',
          },
          {
            sortable: false,
            text: this.$t('customer_id'),
            value: 'customer_id',
            align: 'center',
          },
          {
            sortable: false,
            text: `${this.$t('carrier')} ${this.$t('customer_id')}`,
            value: 'customer_number',
            align: 'center',
          },
          {
            sortable: false,
            text: this.$t('price'),
            value: 'total_price',
            align: 'center',
          },
          {
            sortable: false,
            text: this.$t('weight'),
            value: 'weight',
          },
          {
            sortable: false,
            text: this.$t('created'),
            value: 'created_at',
          },
          {
            value: 'actions',
          },
        ],
        editedItem: {
          title: '',
          package_number: '',
          total_price: '',
          weight: '',
          volume_weight: '',
          carrier: '',
          carrier_product: '',
          carrier_service: '',
          customer_number: '',
          customer_id: '',
          country_from: { id: 194, iso: 'DK', full_name: 'Denmark' },
          country_to: { id: 194, iso: 'DK', full_name: 'Denmark' },
          status: '',
          type: '',
        },
        defaultItem: {
          title: '',
          package_number: '',
          total_price: '',
          weight: '',
          volume_weight: '',
          carrier: '',
          carrier_product: '',
          carrier_service: '',
          customer_number: '',
          customer_id: '',
          country_from: { id: 194, iso: 'DK', full_name: 'Denmark' },
          country_to: { id: 194, iso: 'DK', full_name: 'Denmark' },
          status: '',
          type: '',
        },
        bulkEditedItem: {
          carrier: '',
          carrier_product: '',
          carrier_service: '',
          customer_number: '',
          customer_id: '',
          country_from: null,
          country_to: null,
          status: '',
          type: '',
        },
        bulkDefaultItem: {
          carrier: '',
          carrier_product: '',
          carrier_service: '',
          customer_number: '',
          customer_id: '',
          country_from: null,
          country_to: null,
          status: '',
          type: '',
        },
        filterOptions: {
          package_number: {
            label: this.$t('package_number'),
            type: 'free',
            filter: '',
          },

          filename: {
            label: this.$t('filename'),
            type: 'free',
            filter: '',
          },

          customer_id: {
            label: this.$t('customer_id'),
            type: 'free',
            filter: '',
          },
          customer_number: {
            label: `${this.$t('carrier')} ${this.$t('customer_id')}`,
            type: 'free',
            filter: '',
          },
          total_price: {
            label: this.$t('price'),
            type: 'free',
            filter: '',
          },
          carrier: {
            label: this.$t('carrier'),
            type: 'multi',
            options: vm.$store.state.carriers,
            onlyBox: true,
          },
          status: {
            label: this.$t('status'),
            type: 'multi',
            options: [
              { slug: 'cancelled', label: this.$i18n.t('cancelled') },
              {
                slug: 'customer_not_found',
                label: this.$i18n.t('customer_not_found'),
              },
              { slug: 'error', label: this.$i18n.t('error') },
              { slug: 'invoiced', label: this.$i18n.t('invoiced') },
              {
                slug: 'manual_invoicing',
                label: this.$i18n.t('manual_invoicing'),
              },
              {
                slug: 'missing_erp_number',
                label: this.$i18n.t('missing_erp_number'),
              },
              { slug: 'ok', label: this.$i18n.t('ok') },
              { slug: 'order_not_payed', label: this.$i18n.t('order_not_payed') },
              { slug: 'pallet', label: this.$i18n.t('pallet') },
              {
                slug: 'private_customer',
                label: this.$i18n.t('private_customer'),
              },
              { slug: 'processed', label: this.$i18n.t('processed') },
              { slug: 'ready', label: this.$i18n.t('ready') },
              { slug: 'skipped', label: this.$i18n.t('skipped') },
              { slug: 'transit_label', label: this.$i18n.t('transit_label') },
              { slug: 'duplicate_invoice', label: this.$i18n.t('duplicate_invoice') },
            ],
            filter: [
              'customer_not_found',
              'error',
              'missing_erp_number',
              'order_not_payed',
              'duplicate_invoice',
            ],
            onlyBox: true,
          },
          created_at: {
            label: this.$t('created'),
            type: 'date-range',
            filter: {},
            onlyBox: true,
          },
        },
      }
    },
    mounted: function () {
      this.fetchTypes()
    },
    methods: {
      fetchTypes: function () {
        this.loading = true
        this.$get(`/${this.endpoint}/types`)
          .then(({ data }) => {
            this.types = this.$_.map(data.response_data, (type) => ({
              value: type,
              label: type,
            }))
            this.loading = false
          })
          .catch((e) => {
            this.loading = false
          })
      },
    },
  }
</script>
